import React, { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { LeadDialog } from "@/components/bp/LeadDialog";
import { LeadDrawer } from "@/components/bp/LeadDrawer";
import { LeadBadge, LEAD_ORDER, LEAD_STYLE, fmtDate } from "@/components/bp/bpLabels";

export default function BpLeads() {
  const { me, refresh } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(params.get("new") === "1");
  const leadId = params.get("lead");

  const load = async () => {
    try { const { data } = await api.get("/bp/leads", { params: { status, q: q || undefined } }); setItems(data.items); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openLead = (id) => { params.set("lead", id); params.delete("new"); setParams(params, { replace: true }); };
  const closeLead = () => { params.delete("lead"); setParams(params, { replace: true }); };
  const canCreate = me && !me.error && me.rules?.accepted && !me.must_change_password;

  return (
    <div className="p-6 lg:p-10 space-y-6" data-testid="bp-leads">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-2">// Prospection</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Mes leads</h1>
          <p className="text-sm text-[#495057] mt-2">Chaque prospect vous est attribué dès l'enregistrement. Anti-doublon : SIRET et email sont vérifiés chez tous les partenaires et les clients PAG.</p>
        </div>
        <Button onClick={() => setDialog(true)} disabled={!canCreate} className="h-11 bg-[#002FA7] text-white" data-testid="bp-new-lead"><Plus className="h-4 w-4 mr-2" />Nouveau lead</Button>
      </div>
      {!canCreate && me && !me.error && <div className="border-2 border-[#FFD700] bg-[#FFFBEB] p-3 text-xs" data-testid="bp-leads-locked">Acceptez le règlement Business Partner (et choisissez votre mot de passe) pour enregistrer des leads.</div>}

      <div className="bg-white border border-[#DEE2E6] p-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {[["all", "Tous"], ...LEAD_ORDER.map((k) => [k, LEAD_STYLE[k].label])].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`bp-lead-filter-${k}`}
                    className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 border transition ${status === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>{l}</button>
          ))}
        </div>
        <form className="flex items-center gap-1 ml-auto" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Société, contact, ville…" className="h-8 w-52 text-xs" data-testid="bp-lead-search" />
          <Button type="submit" size="sm" variant="outline" className="h-8" data-testid="bp-lead-search-btn"><Search className="h-3.5 w-3.5" /></Button>
        </form>
      </div>

      <div className="bg-white border border-[#DEE2E6]">
        <div className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-[#868E96]" data-testid="bp-lead-count">{items.length} lead(s)</div>
        {items.length === 0 ? <div className="p-10 text-center text-sm text-[#868E96]" data-testid="bp-lead-empty">Aucun lead dans cette vue.</div> : (
          <div className="divide-y divide-[#DEE2E6]">
            {items.map((l) => (
              <button key={l.id} type="button" onClick={() => openLead(l.id)} className="w-full text-left px-4 py-3 hover:bg-[#F8F9FA] flex flex-wrap items-center gap-3" data-testid={`bp-lead-row-${l.id}`}>
                <span className="text-[10px] font-mono text-[#868E96]">{l.reference}</span>
                <span className="text-sm font-semibold">{l.company_name}</span>
                <span className="text-xs text-[#495057] flex-1 truncate">{l.contact_prenom} {l.contact_nom} · {l.email}{l.ville ? ` · ${l.ville}` : ""}</span>
                <span className="text-[10px] text-[#868E96]">{fmtDate(l.created_at)}</span>
                <LeadBadge status={l.status} testid={`bp-lead-status-${l.id}`} />
              </button>
            ))}
          </div>
        )}
      </div>

      <LeadDialog open={dialog} onOpenChange={(v) => { setDialog(v); if (!v && params.get("new")) { params.delete("new"); setParams(params, { replace: true }); } }} secteurs={me?.secteurs || []} onCreated={() => { load(); refresh?.(); }} />
      <LeadDrawer leadId={leadId} onClose={closeLead} onChanged={() => { load(); refresh?.(); }} />
    </div>
  );
}
